import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useStore } from '../../../../../hooks';
import {
  isValidMail,
  validateMailInputValue,
  validateNameInputValue,
} from '../../../../../utils';
import { IOtherMemberKeys } from './types';
import {
  ContactsLine,
  ContactsWrapper,
  StyledSpan,
  Wrapper,
} from './other-tenant.styled';
import { InputBlock } from '../../../input-block';

interface IProps {
  isSelected: boolean;
  isCollapsed: boolean;
}

export const OtherTenant = observer(({ isSelected, isCollapsed }: IProps) => {
  const { createRequest } = useStore();
  const { requestData, selectedArea } = createRequest;
  const { otherMember, changeOtherMemberParams } = createRequest.applicant;

  const [firstError, setFirstError] = useState<boolean>(false);
  const [lastError, setLastError] = useState<boolean>(false);
  const [patronymicError, setPatronymicError] = useState<boolean>(false);
  const [phoneError, setPhoneError] = useState<boolean>(false);
  const [mailError, setMailError] = useState<boolean>(false);

  useEffect((): void => {
    if (otherMember?.area?.id !== requestData.area?._id) {
      changeOtherMemberParams('area', selectedArea);
    }
  }, [requestData.area?._id, selectedArea]);

  const handleLettersInputChange = (
    key: IOtherMemberKeys,
    value: string
  ): void => {
    const validLetterSymbols = validateNameInputValue(value);
    changeOtherMemberParams(key, validLetterSymbols);

    switch (key) {
      case 'first_name':
        validLetterSymbols.length >= 2 && setFirstError(false);
        break;
      case 'last_name':
        validLetterSymbols.length >= 2 && setLastError(false);
        break;
      case 'patronymic_name':
        validLetterSymbols.length !== 1 && setPatronymicError(false);
        break;
      default:
        break;
    }
  };

  const handleMailInputChange = (
    key: IOtherMemberKeys,
    value: string
  ): void => {
    const validMailSymbols = validateMailInputValue(value);
    changeOtherMemberParams(key, validMailSymbols);
    (!validMailSymbols || isValidMail(validMailSymbols)) && setMailError(false);
  };

  const handlePhoneInputChange = (
    key: IOtherMemberKeys,
    value: string
  ): void => {
    changeOtherMemberParams(key, value);
    value.length === 10 && setPhoneError(false);
  };

  const setErrors = (key: IOtherMemberKeys): void => {
    switch (key) {
      case 'first_name':
        setFirstError(otherMember.first_name.length < 2);
        break;
      case 'last_name':
        setLastError(otherMember.last_name.length < 2);
        break;
      case 'patronymic_name':
        if (otherMember.patronymic_name !== null) {
          setPatronymicError(otherMember.patronymic_name.length === 1);
        }
        break;
      case 'phones':
        setPhoneError(
          (otherMember.phones[0]?.code + otherMember.phones[0]?.number)
            .length !== 10
        );
        break;
      case 'email':
        !!otherMember.email && setMailError(!isValidMail(otherMember.email));
        break;
      default:
        break;
    }
  };

  return (
    <Wrapper isSelected={isSelected} isCollapsed={isCollapsed}>
      <ContactsWrapper isSelected={isSelected} isCollapsed={isCollapsed}>
        <StyledSpan>Иное лицо</StyledSpan>
        <ContactsLine>
          <InputBlock
            placeholder={'Фамилия'}
            value={otherMember.last_name}
            onBlur={() => setErrors('last_name')}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              handleLettersInputChange('last_name', e.target.value || '')
            }
            showError={lastError}
            errorMessage={'Введите не менее 2 символов'}
          />
          <InputBlock
            placeholder={'Имя'}
            value={otherMember.first_name}
            onBlur={() => setErrors('first_name')}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              handleLettersInputChange('first_name', e.target.value || '')
            }
            showError={firstError}
            errorMessage={'Введите не менее 2 символов'}
          />
          <InputBlock
            placeholder={'Отчество (необязательно)'}
            value={otherMember.patronymic_name || ''}
            onBlur={() => setErrors('patronymic_name')}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              handleLettersInputChange('patronymic_name', e.target.value || '')
            }
            showError={patronymicError}
            errorMessage={'Введите не менее 2 символов'}
          />
        </ContactsLine>
        <ContactsLine>
          <InputBlock
            placeholder={'Телефон'}
            value={
              otherMember.phones[0]?.code + otherMember.phones[0]?.number || ''
            }
            onBlur={() => setErrors('phones')}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              handlePhoneInputChange('phones', e.target.value || '')
            }
            showError={phoneError}
            errorMessage={'Некорректный номер телефона'}
          />
          <InputBlock
            placeholder={'Email (необязательно)'}
            value={otherMember.email || ''}
            onBlur={() => setErrors('email')}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              handleMailInputChange('email', e.target.value || '')
            }
            showError={mailError}
            errorMessage={'Некорректный email'}
          />
        </ContactsLine>
      </ContactsWrapper>
    </Wrapper>
  );
});
